import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Plus, Trash2, Save, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_TIERS = [
  {
    hours_before_checkin: 720, // 30 days
    fee_type: 'percentage',
    fee_value: 0,
    description: 'Free cancellation 30+ days before arrival'
  },
  {
    hours_before_checkin: 168, // 7 days
    fee_type: 'percentage',
    fee_value: 30,
    description: '30-7 days before: 30% cancellation fee'
  },
  {
    hours_before_checkin: 72, // 3 days
    fee_type: 'percentage',
    fee_value: 70,
    description: '7-3 days before: 70% cancellation fee'
  },
  {
    hours_before_checkin: 0, // Less than 3 days / No-show
    fee_type: 'percentage',
    fee_value: 100,
    description: 'Less than 3 days or no-show: 100% cancellation fee'
  }
];

export default function CancellationPolicySettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [policy, setPolicy] = useState({
    enabled: true,
    default_policy_type: 'hotel_kaiser',
    tiers: DEFAULT_TIERS
  });

  useEffect(() => {
    fetchPolicy();
  }, []);

  const fetchPolicy = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/admin/settings/cancellation-policy`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPolicy(data);
      }
    } catch (error) {
      console.error('Error fetching policy:', error);
      toast.error('Failed to load cancellation policy');
    } finally {
      setLoading(false);
    }
  };

  const savePolicy = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/admin/settings/cancellation-policy`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(policy)
      });

      if (response.ok) {
        toast.success('Cancellation policy saved successfully');
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      console.error('Error saving policy:', error);
      toast.error('Failed to save cancellation policy');
    } finally {
      setSaving(false);
    }
  };

  const addTier = () => {
    setPolicy({
      ...policy,
      tiers: [
        ...policy.tiers,
        {
          hours_before_checkin: 24,
          fee_type: 'nights',
          fee_value: 1,
          description: 'New tier'
        }
      ]
    });
  };

  const removeTier = (index) => {
    const newTiers = [...policy.tiers];
    newTiers.splice(index, 1);
    setPolicy({ ...policy, tiers: newTiers });
  };

  const updateTier = (index, field, value) => {
    const newTiers = [...policy.tiers];
    newTiers[index] = { ...newTiers[index], [field]: value };
    setPolicy({ ...policy, tiers: newTiers });
  };

  const applyPreset = (preset) => {
    const presets = {
      hotel_kaiser: [
        { hours_before_checkin: 720, fee_type: 'percentage', fee_value: 0, description: 'Free cancellation 30+ days before arrival' },
        { hours_before_checkin: 168, fee_type: 'percentage', fee_value: 30, description: '30-7 days before: 30% cancellation fee' },
        { hours_before_checkin: 72, fee_type: 'percentage', fee_value: 70, description: '7-3 days before: 70% cancellation fee' },
        { hours_before_checkin: 0, fee_type: 'percentage', fee_value: 100, description: 'Less than 3 days or no-show: 100% cancellation fee' }
      ],
      flexible: [
        { hours_before_checkin: 24, fee_type: 'percentage', fee_value: 0, description: 'Free cancellation up to 24 hours before check-in' },
        { hours_before_checkin: 0, fee_type: 'nights', fee_value: 1, description: 'Less than 24 hours: 1 night fee' }
      ],
      moderate: [
        { hours_before_checkin: 120, fee_type: 'percentage', fee_value: 0, description: 'Free cancellation up to 5 days before check-in' },
        { hours_before_checkin: 24, fee_type: 'nights', fee_value: 1, description: '1-5 days before: 1 night fee' },
        { hours_before_checkin: 0, fee_type: 'percentage', fee_value: 50, description: 'Less than 24 hours: 50% of booking' }
      ],
      strict: [
        { hours_before_checkin: 168, fee_type: 'percentage', fee_value: 50, description: '7+ days before: 50% refund' },
        { hours_before_checkin: 48, fee_type: 'percentage', fee_value: 0, description: '2-7 days before: No refund' },
        { hours_before_checkin: 0, fee_type: 'percentage', fee_value: 100, description: 'Less than 48 hours: Full charge' }
      ]
    };
    
    setPolicy({
      ...policy,
      default_policy_type: preset,
      tiers: presets[preset] || presets.hotel_kaiser
    });
    toast.info(`Applied ${preset === 'hotel_kaiser' ? 'Hotel Kaiser' : preset} policy preset`);
  };

  const formatHours = (hours) => {
    if (hours >= 24) {
      const days = hours / 24;
      return `${days} day${days > 1 ? 's' : ''}`;
    }
    return `${hours} hour${hours > 1 ? 's' : ''}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <span>📋</span> Cancellation Policy
        </CardTitle>
        <CardDescription>
          Configure cancellation fees based on time before check-in
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Enable/Disable */}
        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
          <div>
            <Label className="font-medium">Policy Enabled</Label>
            <p className="text-sm text-gray-500">Apply cancellation fees based on policy rules</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={policy.enabled}
              onChange={(e) => setPolicy({ ...policy, enabled: e.target.checked })}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>

        {/* Presets */}
        <div>
          <Label className="mb-2 block">Quick Presets</Label>
          <div className="flex gap-2 flex-wrap">
            <Button
              variant={policy.default_policy_type === 'hotel_kaiser' ? 'default' : 'outline'}
              size="sm"
              onClick={() => applyPreset('hotel_kaiser')}
            >
              Hotel Kaiser
            </Button>
            <Button
              variant={policy.default_policy_type === 'flexible' ? 'default' : 'outline'}
              size="sm"
              onClick={() => applyPreset('flexible')}
            >
              Flexible
            </Button>
            <Button
              variant={policy.default_policy_type === 'moderate' ? 'default' : 'outline'}
              size="sm"
              onClick={() => applyPreset('moderate')}
            >
              Moderate
            </Button>
            <Button
              variant={policy.default_policy_type === 'strict' ? 'default' : 'outline'}
              size="sm"
              onClick={() => applyPreset('strict')}
            >
              Strict
            </Button>
          </div>
        </div>

        {/* Policy Tiers */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label className="font-medium">Policy Tiers</Label>
            <Button variant="outline" size="sm" onClick={addTier}>
              <Plus className="w-4 h-4 mr-1" /> Add Tier
            </Button>
          </div>

          {policy.tiers
            .sort((a, b) => b.hours_before_checkin - a.hours_before_checkin)
            .map((tier, index) => (
              <div key={index} className="border rounded-lg p-4 space-y-3 bg-white">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-sm text-blue-600">
                    Tier {index + 1}: {tier.hours_before_checkin === 0 ? 'Last minute' : `${formatHours(tier.hours_before_checkin)}+ before check-in`}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-500 hover:text-red-700"
                    onClick={() => removeTier(index)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs text-gray-500">Hours Before Check-in</Label>
                    <Input
                      type="number"
                      min="0"
                      value={tier.hours_before_checkin}
                      onChange={(e) => updateTier(index, 'hours_before_checkin', parseInt(e.target.value) || 0)}
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Fee Type</Label>
                    <select
                      className="w-full border rounded-md p-2 text-sm"
                      value={tier.fee_type}
                      onChange={(e) => updateTier(index, 'fee_type', e.target.value)}
                    >
                      <option value="percentage">Percentage of Total</option>
                      <option value="nights">Number of Nights</option>
                      <option value="fixed">Fixed Amount (€)</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">
                      {tier.fee_type === 'percentage' ? 'Percentage (%)' : 
                       tier.fee_type === 'nights' ? 'Nights' : 'Amount (€)'}
                    </Label>
                    <Input
                      type="number"
                      min="0"
                      step={tier.fee_type === 'percentage' ? '1' : tier.fee_type === 'fixed' ? '0.01' : '1'}
                      value={tier.fee_value}
                      onChange={(e) => updateTier(index, 'fee_value', parseFloat(e.target.value) || 0)}
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-xs text-gray-500">Description (shown to guests)</Label>
                  <Input
                    value={tier.description}
                    onChange={(e) => updateTier(index, 'description', e.target.value)}
                    placeholder="e.g., Free cancellation up to 24 hours before check-in"
                  />
                </div>
              </div>
            ))}
        </div>

        {/* Preview */}
        <div className="bg-blue-50 p-4 rounded-lg">
          <Label className="font-medium text-blue-800 mb-2 block">Policy Preview</Label>
          <ul className="space-y-1 text-sm text-blue-700">
            {policy.tiers
              .sort((a, b) => b.hours_before_checkin - a.hours_before_checkin)
              .map((tier, index) => (
                <li key={index} className="flex items-start gap-2">
                  <span>•</span>
                  <span>
                    <strong>{tier.hours_before_checkin === 0 ? 'Last minute' : `${formatHours(tier.hours_before_checkin)}+ before:`}</strong>{' '}
                    {tier.fee_value === 0 ? 'Free cancellation' :
                     tier.fee_type === 'percentage' ? `${tier.fee_value}% fee` :
                     tier.fee_type === 'nights' ? `${tier.fee_value} night${tier.fee_value > 1 ? 's' : ''} fee` :
                     `€${tier.fee_value} fee`}
                  </span>
                </li>
              ))}
          </ul>
        </div>

        {/* Save Button */}
        <div className="flex justify-end pt-4 border-t">
          <Button onClick={savePolicy} disabled={saving}>
            {saving ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Policy
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
